import styled, { css, keyframes } from "styled-components";

const timelineRefreshKeyframes = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
    filter: saturate(0.92);
  }

  to {
    opacity: 1;
    transform: translateY(0);
    filter: saturate(1);
  }
`;

const timelineRefresh = css`
  animation: ${timelineRefreshKeyframes} 180ms cubic-bezier(0.2, 0.8, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const Container = styled.div`
  flex: 1;
  position: relative;
  /* background: #e4dcd4; */
  overflow-y: scroll;
  overflow-x: hidden;
  padding: 20px 5% 2pc;
  min-height: 0;
  min-height: 100px;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;

  &::after {
    content: "";
    display: block;
    height: 1px;
  }

  .icon {
    color: ${(props) => props.theme.common.subHeadingColor};
    margin-right: 5px;
    margin-bottom: -1px;
  }
`;

export const wrapperStyles = css`
  z-index: 9;
`;

export const DateWrapper = styled.div`
  text-align: center;
  margin: 10px 0 14px;
  position: relative;

  ${wrapperStyles}
`;

export const Date = styled.span`
  background: ${(props) => props.theme.badge.bgColor};
  display: inline-block;
  color: ${(props) => props.theme.badge.textColor};
  font-size: 0.75rem;
  padding: 7px 10px;
  border-radius: 8px;
  direction: rtl;
  unicode-bidi: plaintext;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.06);
`;

export const StickyDate = styled.div`
  position: sticky;
  top: 8px;
  display: flex;
  justify-content: center;
  z-index: 20;
  pointer-events: none;
`;

export const EncryptionMessage = styled.div`
  background: ${(props) => props.theme.encryptionMessage.bgColor};
  color: ${(props) => props.theme.encryptionMessage.textColor};
  font-size: 0.77rem;
  text-align: center;
  direction: rtl;
  padding: 5px 10px;
  position: relative;
  margin-bottom: 8px;
  border-radius: 8px;
  line-height: 20px;

  ${wrapperStyles}
`;

export const MessageGroup = styled.div`
  ${wrapperStyles}
  ${timelineRefresh}

  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
  position: relative;
  direction: ltr;

  .chat__msg--sent {
    background: ${(props) => props.theme.sentMessage.bgColor};
    align-self: flex-end;
    --bubble-bg: ${(props) => props.theme.sentMessage.bgColor};
  }

  .chat__msg--received {
    background: ${(props) => props.theme.receivedMessage.bgColor};
    align-self: flex-start;
    --bubble-bg: ${(props) => props.theme.receivedMessage.bgColor};
  }

  .chat__msg-status-icon {
    color: ${(props) => props.theme.common.subHeadingColor};
    margin-left: 3px;
  }

  .chat__msg-status-icon--blue {
    color: ${(props) => props.theme.common.readIconColor};
  }
`;

export const ChatMessage = styled.div`
  padding: 8px 12px;
  margin-bottom: 12px;
  font-size: 0.85rem;
  color: ${(props) => props.theme.common.mainHeadingColor};
  width: fit-content;
  max-width: 95%;
  line-height: 20px;
  border-radius: 12px;
  position: relative;
  white-space: pre-line;
  display: flex;
  flex-direction: column;
  gap: 4px;
  direction: rtl;
  text-align: right;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.04);
  padding-top: 8px;

  &.is-grouped {
    margin-bottom: 4px;
  }

  @media screen and (min-width: 1301px) {
    max-width: 65%;
  }

  @media screen and (min-width: 1000px) and (max-width: 1300px) {
    max-width: 75%;
  }

  @media screen and (min-width: 900px) and (max-width: 1000px) {
    max-width: 85%;
  }

  &.has-tail::after {
    content: "";
    position: absolute;
    bottom: 3px;
    width: 16px;
    height: 12px;
    background: radial-gradient(
      8px at top right,
      transparent 99%,
      var(--bubble-bg) 102%
    );
    pointer-events: none;
  }

  &.has-tail.chat__msg--sent::after {
    right: -8px;
  }

  &.has-tail.chat__msg--received::before {
    content: "";
    position: absolute;
    bottom: 3px;
    left: -8px;
    width: 16px;
    height: 12px;
    background: radial-gradient(
      8px at top left,
      transparent 99%,
      var(--bubble-bg) 102%
    );
    pointer-events: none;
  }
`;

export const ForwardedLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.7rem;
  color: ${(props) => props.theme.common.subHeadingColor};
  align-self: flex-start;
  margin-bottom: 2px;
  direction: ltr;
  text-align: left;
`;

export const ForwardedIcon = styled.img`
  width: 14px;
  height: 14px;
  object-fit: contain;
  display: inline-block;
`;

export const ChatMessageFooter = styled.span`
  display: flex;
  align-items: center;
  align-self: flex-start;
  margin-inline-start: 0;
  margin-inline-end: auto;
  gap: 4px;
  margin-top: 2px;
  color: ${(props) => props.theme.common.subHeadingColor};
  font-size: 0.72rem;
  font-weight: 500;
  direction: ltr;
  text-align: right;
`;

export const InfoMessage = styled(EncryptionMessage).attrs({ className: "chat__msg--info" })`
  align-self: center;
  max-width: 80%;
  direction: rtl;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 12px;
`;

export const EmptyState = styled(InfoMessage)`
  ${timelineRefresh}

  gap: 3px;
  margin-top: 18px;
`;

export const EmptyStateText = styled.span`
  text-align: center;
  line-height: 1.45;
`;

export const InfoText = styled.span`
  text-align: center;
  line-height: 1.35;

  a {
    color: ${(props) => props.theme.common.readIconColor};
    text-decoration: underline;
  }
`;

export const CallMessage = styled(InfoMessage)`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-align: right;
  padding: 6px 10px;
`;

export const CallIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.common.subHeadingColor};
`;

export const InfoPreviewLink = styled.a`
  text-decoration: none;
  color: inherit;
  width: 100%;
`;

export const InfoPreviewCard = styled.div`
  background: ${(props) => props.theme.common.secondaryColor};
  border: 1px solid ${(props) => props.theme.common.borderColor};
  border-radius: 10px;
  overflow: hidden;
  text-align: right;
  direction: rtl;
`;

export const InfoPreviewImage = styled.img`
  width: 100%;
  height: 140px;
  object-fit: cover;
  display: block;
`;

export const InfoPreviewBody = styled.div`
  padding: 8px 10px 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const InfoPreviewTitle = styled.div`
  font-size: 0.82rem;
  font-weight: 600;
  color: ${(props) => props.theme.common.mainHeadingColor};
`;

export const InfoPreviewDescription = styled.div`
  font-size: 0.75rem;
  color: ${(props) => props.theme.common.subHeadingColor};
  line-height: 1.35;
`;

export const InfoPreviewSite = styled.div`
  font-size: 0.7rem;
  color: ${(props) => props.theme.common.subHeadingColor};
  direction: ltr;
  text-align: left;
`;

export const MessagePreviewLink = styled.a`
  text-decoration: none;
  color: inherit;
  width: 100%;
`;

export const MessagePreviewCard = styled.div`
  background: ${(props) => props.theme.common.secondaryColor};
  border: 1px solid ${(props) => props.theme.common.borderColor};
  border-radius: 10px;
  overflow: hidden;
  text-align: right;
  direction: rtl;
  max-width: 300px;
`;

export const MessagePreviewImage = styled.img`
  width: 100%;
  height: 140px;
  object-fit: cover;
  display: block;
`;

export const MessagePreviewBody = styled.div`
  padding: 8px 10px 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const MessagePreviewTitle = styled.div`
  font-size: 0.82rem;
  font-weight: 600;
  color: ${(props) => props.theme.common.mainHeadingColor};
`;

export const MessagePreviewDescription = styled.div`
  font-size: 0.75rem;
  color: ${(props) => props.theme.common.subHeadingColor};
  line-height: 1.35;
`;

export const MessagePreviewSite = styled.div`
  font-size: 0.7rem;
  color: ${(props) => props.theme.common.subHeadingColor};
  direction: ltr;
  text-align: left;
`;

export const ContactCard = styled.div`
  background: ${(props) => props.theme.common.secondaryColor};
  border: 1px solid ${(props) => props.theme.common.borderColor};
  border-radius: 10px;
  overflow: hidden;
  min-width: 220px;
  max-width: 320px;
`;

export const ContactCardBody = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
`;

export const ContactCardAvatar = styled.img`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

export const ContactCardIcon = styled.span`
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 52px;
    height: 52px;
  }
`;

export const ContactCardText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const ContactCardName = styled.div`
  font-size: 0.84rem;
  font-weight: 600;
  color: ${(props) => props.theme.common.mainHeadingColor};
`;

export const ContactCardLabel = styled.div`
  font-size: 0.73rem;
  color: ${(props) => props.theme.common.subHeadingColor};
`;

export const ContactCardFooter = styled.div`
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  padding: 8px 10px;
  font-size: 0.73rem;
  color: ${(props) => props.theme.common.readIconColor};
  text-align: center;
`;
